import { Injectable } from '@angular/core';
import {
  CrudAPI,
  CrudService,
  TranslateGenericApiValidationError,
} from '@core/api';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  of,
  share,
  shareReplay,
  Subject,
  switchMap,
  throwError,
} from 'rxjs';
// Known leak, ratcheted rather than excused: this value import puts
// DevExtreme's runtime in the NativeScript bundle, measured at 1.53 MiB (9.3%
// of vendor.js) in docs/analysis/mobile-bundle-devextreme-leak.md. The rule errors on
// any NEW devextreme import here; removing these eight disables is what fixing
// that leak means.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import notify from 'devextreme/ui/notify';
import { TranslateService } from '@ngx-translate/core';
import { status, accounting_status, pdfs, project_types } from '../datas';
import {
  ProjectBaseInfo,
  ProjectInfo,
  ProjectLocation,
  ProjectLocationChange,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  public projectApi!: CrudAPI<ProjectInfo>;
  public projectLocationApi!: CrudAPI<ProjectLocation>;
  private projects = new BehaviorSubject<ProjectInfo[]>([]);
  private simpleProjects = new BehaviorSubject<ProjectInfo[]>([]);
  public project = new BehaviorSubject<ProjectInfo>({} as ProjectInfo);
  public projectbase = new BehaviorSubject<ProjectBaseInfo>(
    {} as ProjectBaseInfo
  );
  private projectLocations = new BehaviorSubject<ProjectLocation[]>([]);
  private projectSearch = new Subject<any[]>();
  private reportContentApi!: CrudAPI<Record<string, string>>;

  public projects$ = this.projects.asObservable();
  public simpleProjects$ = this.simpleProjects.asObservable();
  public project$ = this.project.asObservable();
  public projectBase$ = this.projectbase.asObservable();
  public projectLocations$ = this.projectLocations.asObservable();
  public readonly searchResult$ = this.projectSearch.pipe(
    distinctUntilChanged(),
    filter((x) => x[0].length >= 3),
    switchMap(([query, userId]) =>
      this.crud.readMany(
        `api/component/project/projectsAutoComplete?search=${query}&userId=${userId}`
      )
    ),
    map(({ data }: any) => data), // transform data if necessary
    share()
  );

  constructor(
    private crud: CrudService,
    private translateService: TranslateService,
    private translateGenericApiValidationError: TranslateGenericApiValidationError
  ) {
    this.projectApi = crud.forEndpoint('api/crud/project');
    this.projectLocationApi = crud.forEndpoint('api/crud/projectLocation');
    this.reportContentApi = crud.forEndpoint(
      'api/component/project/updateProjectReportContent'
    );
  }

  public projectTypes$ = of(project_types).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => project_types.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public status$ = of(status).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => status.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public pdfs$ = of(pdfs).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => pdfs.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public accountingStatus$ = of(accounting_status).pipe(
    mergeMap((x) => this.translateService.get(x.map((u) => u.label))),
    map((x) => accounting_status.map((u) => ({ ...u, label: x[u.label] }))),
    shareReplay(1)
  );

  public loadProjects() {
    this.projectApi
      .readMany(`with=clientCompany%3BclientContactPerson`)
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as ProjectInfo[]);
        })
      )
      .subscribe((x) => this.projects.next(x));
  }

  public searchWithoutUser(input: string, userId: number | null) {
    this.projectSearch.next([input, userId]);
  }

  public searchProjects(search: string) {
    return this.crud
      .readMany(`api/component/project/projectsAutoComplete?search=` + search)
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as ProjectInfo[]);
        })
      );
  }

  public loadProject(id: number) {
    this.projectApi
      .readOne(id, 'with=users%3Busers.userDetails%3BprojectImageFile')
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of(null);
        })
      )
      .subscribe((x) => this.project.next(x));
  }
  public loadProjectBaseInfo(id: number) {
    this.crud
      .forEndpoint('api/component/project/projectBaseInfo?project_id=' + id)
      .readMany()
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of(null);
        })
      )
      .subscribe((x) => this.projectbase.next(x));
  }

  public loadProjectLocations(projectId?: number) {
    if (typeof projectId === 'number') {
      this.projectLocationApi
        .readMany(`search=project_id:${projectId}&searchFields=project_id:=`)
        .pipe(
          map(({ data }: any) => data),
          catchError((e) => {
            notify(e.message, 'error');
            return of([] as ProjectLocation[]);
          })
        )
        .subscribe((x) => this.projectLocations.next(x));
    } else {
      this.projectLocations.next([]);
    }
  }

  public updateProjectLocations(
    projectId: number,
    updates: ProjectLocationChange[]
  ) {
    from(updates)
      .pipe(
        concatMap((update) => {
          const { id, created_at, deleted_at, updated_at, ...data } =
            update.data as ProjectLocation;
          return update.type === 'insert'
            ? this.projectLocationApi.createOne({
                ...data,
                project_id: projectId,
              })
            : update.type === 'remove'
            ? this.projectLocationApi.deleteOne(id)
            : this.projectLocationApi.updateOne(id, data);
        }),
        finalize(() => this.loadProjectLocations(projectId))
      )
      .subscribe();
  }

  public updateProject(itemId: number, data: Partial<ProjectInfo>) {
    data.report_content = JSON.stringify(data.report_content);
    this.projectApi
      .updateOne(itemId, {
        ...data,
        //@ts-expect-error // no index signature for 'users'
        users:
          data.users instanceof Array ? data.users.map((x) => x.id) : undefined,
      })
      .pipe(
        catchError((e) => {
          if (Object.prototype.hasOwnProperty.call(e, 'error')) {
            const errors = this.translateGenericApiValidationError.translate(e);
            notify(errors, 'error', 10000);
          }
          throw e;
        })
      )
      .subscribe({
        next: (res: any) => {
          this.loadProject(itemId as number);
          notify(this.translateService.instant(res.message), 'success');
        },
      });
  }

  public updateProjectList(itemId: number, data: Partial<ProjectInfo>) {
    this.projectApi.updateOne(itemId, data).subscribe({
      error: (e) => notify(e.message, 'error'),
    });
  }

  public createProject(data: ProjectInfo) {
    data.report_content = JSON.stringify(data.report_content);
    return this.projectApi.createOne(data).pipe(
      catchError((e) => {
        if (Object.prototype.hasOwnProperty.call(e, 'error')) {
          const errors = this.translateGenericApiValidationError.translate(e);
          notify(errors, 'error', 10000);
        }
        throw e;
      })
    );
  }
  public updateProjectReportContent(
    projectId: number,
    data: Record<string, string>
  ) {
    return this.reportContentApi.updateOne(projectId, data).pipe(
      catchError((e) => {
        notify(e.message, 'error');
        return of(null);
      })
    );
  }
}
